import { memo, useEffect, useRef, useState } from "react";

import mermaid, { type RenderResult } from "mermaid";

import { cn } from "@virtual-brain-lib/frontend-design-system/utils";

export interface MermaidDiagramProps {
	readonly mermaidCode: string;
	readonly className?: string;
	readonly containerId: string;
}

const diagramCache = new Map<string, RenderResult>();

function MermaidRenderer({ mermaidCode, className, containerId }: MermaidDiagramProps) {
	const elementRef = useRef<HTMLDivElement>(null);
	const [renderResult, setRenderResult] = useState<RenderResult>();

	useEffect(() => {
		if (!elementRef.current) {
			return;
		}

		if (!renderResult?.svg && elementRef.current) {
			elementRef.current.innerHTML = "Failed to load diagram.";

			return;
		}

		if (renderResult) {
			elementRef.current.innerHTML = renderResult.svg;
			renderResult.bindFunctions?.(elementRef.current);
		}
	}, [renderResult]);

	useEffect(() => {
		if (mermaidCode?.length === 0) {
			return;
		}

		const cachedResult = diagramCache.get(mermaidCode);

		if (cachedResult) {
			setRenderResult(cachedResult);

			return;
		}

		(async () => {
			try {
				if ((await mermaid.parse(mermaidCode, { suppressErrors: true })) === false) {
					if (elementRef.current) {
						elementRef.current.innerHTML = "Failed to load diagram.";
					}

					// remove unwanted error divs created by mermaid
					const dmSvgDiv = document.querySelector(`#dm-${containerId}-svg`);
					const dSvgDiv = document.querySelector("#d-svg");

					dmSvgDiv?.remove();
					dSvgDiv?.remove();

					return;
				}

				const rr = await mermaid.render(`${containerId}-svg`, mermaidCode);

				diagramCache.set(mermaidCode, rr);
				setRenderResult(rr);
			} catch {
				if (elementRef.current) {
					elementRef.current.innerHTML = "Failed to load diagram.";
				}

				// remove unwanted error divs created by mermaid
				const dmSvgDiv = document.querySelector(`#dm-${containerId}-svg`);
				const dSvgDiv = document.querySelector("#d-svg");

				dmSvgDiv?.remove();
				dSvgDiv?.remove();
			}
		})();
	}, [mermaidCode]);

	return (
		<div
			ref={elementRef}
			className={cn("mermaid flex h-full w-full items-center justify-center", className)}
			id={containerId}
		/>
	);
}

export default memo(MermaidRenderer);
